import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight, Globe, Check, MapPin, Mail } from "lucide-react";
import { SITE } from "../data/site";
import { useLang, LANGUAGES } from "../i18n";
import { SoundToggle } from "./SoundToggle";
import { playBlip } from "../utils/sound";

const EASE = [0.22, 1, 0.36, 1];

const LangMenu = () => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="language-menu-button"
        aria-label="Change language"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors duration-300 hover:border-ink/45"
      >
        <Globe className="h-4.5 w-4.5" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            data-testid="language-menu"
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="absolute end-0 top-12 z-50 w-44 overflow-hidden rounded-2xl border border-sand bg-espresso p-1.5 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)]"
          >
            {LANGUAGES.map((l) => (
              <li key={l.code}>
                <button
                  type="button"
                  data-testid={`language-option-${l.code}`}
                  onClick={() => { setLang(l.code); setOpen(false); }}
                  className={`flex w-full items-center justify-between rounded-xl px-4 py-2.5 text-sm transition-colors ${
                    lang === l.code
                      ? "bg-ink text-paper font-semibold"
                      : "text-ink/75 hover:bg-ink/10 hover:text-ink"
                  } ${l.code === "am" || l.code === "ti" ? "font-ethiopic" : ""} ${l.code === "ar" ? "font-arabic-body" : ""}`}
                >
                  {l.native}
                  {lang === l.code && <Check className="h-4 w-4" />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

// Shared link table; prefix keeps testids unique between header bar and overlay.
const navLinks = (t, prefix) => [
  { to: "/", label: t("nav.home"), testid: `${prefix}-home`, num: "01" },
  { to: "/repair-lab", label: t("nav.lab"), testid: `${prefix}-repair-lab`, num: "02" },
  { to: "/phones", label: t("nav.phones"), testid: `${prefix}-phones`, num: "03" },
  { to: "/showroom", label: t("nav.showroom"), testid: `${prefix}-showroom`, num: "04" },
  { to: "/visit", label: t("nav.visit"), testid: `${prefix}-visit`, num: "05" },
  { to: "/quote", label: t("nav.getEstimate"), testid: `${prefix}-quote`, num: "06", accent: true },
];

// Inline desktop header link: numbered, sliding underline, active state.
const HeaderLink = ({ link }) => {
  const location = useLocation();
  const active = location.pathname === link.to;
  return (
    <Link
      to={link.to}
      data-testid={link.testid}
      onClick={() => playBlip(500)}
      className={`group relative whitespace-nowrap py-1 text-sm font-medium tracking-wide transition-colors duration-300 ${
        active ? "text-ink" : "text-ink/60 hover:text-ink"
      }`}
    >
      <span className={`me-1 font-mono text-[9px] align-super transition-colors duration-300 ${active ? "text-volt" : "text-accent-soft/0 group-hover:text-accent-soft"}`}>
        {link.num}
      </span>
      {link.label}
      <span
        aria-hidden="true"
        className={`absolute -bottom-0.5 left-0 h-[2px] bg-volt transition-[width] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          active ? "w-full" : "w-0 group-hover:w-full"
        }`}
      />
    </Link>
  );
};

// Numbered, edge-staged full-screen navigation — motion-studio style (mobile).
const MenuOverlay = ({ onClose }) => {
  const { t, lang, setLang } = useLang();
  const location = useLocation();
  const reduce = useReducedMotion();
  const firstLink = useRef(null);

  const LINKS = navLinks(t, "mobile-nav-link");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    firstLink.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      data-testid="mobile-menu-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      initial={reduce ? { opacity: 0 } : { clipPath: "inset(0 0 100% 0 round 40px)" }}
      animate={reduce ? { opacity: 1 } : { clipPath: "inset(0 0 0% 0 round 0px)" }}
      exit={reduce ? { opacity: 0 } : { clipPath: "inset(0 0 100% 0 round 40px)" }}
      transition={{ duration: 0.65, ease: EASE }}
      className="grain fixed inset-0 z-[60] flex flex-col bg-espresso text-ink"
    >
      <div className="pointer-events-none absolute -top-32 right-[8%] h-[22rem] w-[22rem] rounded-full bg-volt/12 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-[-6rem] left-[4%] h-[20rem] w-[20rem] rounded-full bg-magenta/12 blur-3xl" aria-hidden="true" />

      <div className="relative flex items-center justify-between px-4 sm:px-8 py-4">
        <span className="aura-logo inline-flex items-center gap-3">
          <img src={SITE.logo} alt="" className="h-9 w-9 rounded-full object-cover" />
          <span className="font-display text-lg font-semibold">Next-G</span>
        </span>
        <button
          type="button"
          data-testid="mobile-menu-close"
          aria-label={t("nav.closeMenu")}
          onClick={onClose}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/25 transition-colors hover:border-volt hover:text-volt"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="relative flex flex-1 flex-col justify-center gap-2 overflow-y-auto px-6 sm:px-12 lg:flex-row lg:items-center lg:gap-16 lg:px-20">
        <nav aria-label="Menu" className="flex flex-col">
          {LINKS.map((l, i) => {
            const active = location.pathname === l.to;
            return (
              <motion.div
                key={l.to}
                initial={reduce ? false : { opacity: 0, y: 54, rotate: 2.5 }}
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                transition={{ delay: 0.18 + i * 0.07, duration: 0.7, ease: EASE }}
              >
                <Link
                  to={l.to}
                  ref={i === 0 ? firstLink : undefined}
                  data-testid={l.testid}
                  onClick={() => { playBlip(460 + i * 60); onClose(); }}
                  className={`group flex items-baseline gap-4 py-2.5 sm:py-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight transition-colors duration-300 ${
                    l.accent
                      ? "text-volt hover:text-accent-bright"
                      : active
                        ? "text-gold-soft"
                        : "text-ink/85 hover:text-ink"
                  }`}
                >
                  <span className={`font-mono text-xs sm:text-sm transition-colors duration-300 ${active ? "text-volt" : "text-accent-soft group-hover:text-volt"}`}>
                    {l.num}
                  </span>
                  <span className="relative">
                    {l.label}
                    <motion.span
                      aria-hidden="true"
                      className={`absolute -bottom-1 left-0 h-[3px] bg-volt ${active ? "w-full" : "w-0 group-hover:w-full"}`}
                      style={{ transition: "width 0.45s cubic-bezier(0.22,1,0.36,1)" }}
                    />
                  </span>
                  <ArrowUpRight className="h-6 w-6 self-center text-ink/0 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-accent-bright rtl:-scale-x-100" />
                </Link>
              </motion.div>
            );
          })}
        </nav>

        <motion.aside
          initial={reduce ? false : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.7, ease: EASE }}
          className="mt-8 w-full max-w-sm space-y-5 lg:mt-0"
        >
          <a
            href={SITE.phoneHref}
            data-testid="mobile-call-button"
            className="flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-semibold text-[#EDF1F7] transition-colors duration-300 hover:bg-volt hover:text-[#0B111B]"
          >
            <Phone className="h-4 w-4" /> {t("nav.call")} {SITE.phoneDisplay}
          </a>
          <div className="space-y-2 text-sm text-ink/65">
            <p className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-magenta" />
              {SITE.addressShort} · {SITE.locationCue}
            </p>
            <a href={SITE.emailHref} className="flex items-center gap-2 transition-colors hover:text-ink">
              <Mail className="h-4 w-4 text-cyan" />
              {SITE.email}
            </a>
          </div>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                type="button"
                data-testid={`mobile-language-option-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                  lang === l.code
                    ? "border-gold bg-gold text-espresso font-bold"
                    : "border-ink/25 text-ink/75 hover:border-ink/60"
                } ${l.code === "am" || l.code === "ti" ? "font-ethiopic" : ""} ${l.code === "ar" ? "font-arabic-body" : ""}`}
              >
                {l.native}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 text-ink/55">
            <SoundToggle testid="menu-sound-toggle" />
            <span className="font-mono text-[10px] uppercase tracking-[0.24em]">{t("nav.sound")}</span>
          </div>
        </motion.aside>
      </div>

      <motion.p
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.6 }}
        className="relative px-8 pb-6 pt-3 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-ink/45"
      >
        {SITE.motto}
      </motion.p>
    </motion.div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const reduce = useReducedMotion();
  const menuBtn = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (!open) menuBtn.current?.focus({ preventScroll: true });
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={reduce ? false : { y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-paper/85 backdrop-blur-md border-b border-sand"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16 py-3 sm:py-4">
          <Link to="/" data-testid="header-brand-link" className="group flex items-center gap-3">
            <span className="aura-logo inline-flex">
              <img
                src={SITE.logo}
                alt="Next-G Phone Repair logo"
                className="h-12 w-12 sm:h-14 sm:w-14 rounded-full object-cover ring-1 ring-gold/40 transition-transform duration-500 group-hover:rotate-[15deg]"
              />
            </span>
            <span className="leading-none">
              <span className="block font-display text-xl sm:text-2xl font-semibold tracking-tight">
                Next-G
              </span>
              <span className="mt-1 block font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.24em] text-ink/55">
                {t("nav.tagline")}
              </span>
            </span>
          </Link>

          {/* inline desktop navigation — links side by side next to the brand */}
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks(t, "nav-link").slice(0, 5).map((l) => (
              <HeaderLink key={l.to} link={l} />
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={SITE.phoneHref}
              data-testid="header-phone-link"
              className="hidden xl:flex items-center gap-2 whitespace-nowrap font-mono text-xs tracking-widest text-ink/70 transition-colors hover:text-accent-bright"
            >
              <Phone className="h-3.5 w-3.5" />
              {SITE.phoneDisplay}
            </a>
            <LangMenu />
            <Link
              to="/quote"
              data-testid="nav-quote-cta"
              className="group hidden sm:inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-accent hover:text-[#EDF1F7]"
            >
              {t("nav.getEstimate")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
            </Link>
            <button
              ref={menuBtn}
              type="button"
              data-testid="mobile-menu-button"
              aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
              aria-expanded={open}
              onClick={() => { playBlip(open ? 380 : 520); setOpen((o) => !o); }}
              className="group inline-flex h-10 items-center gap-2 rounded-full border border-ink/15 px-4 text-ink transition-colors duration-300 hover:border-volt hover:text-volt lg:hidden"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.22em]">{t("nav.menu")}</span>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5 transition-transform duration-300 group-hover:rotate-90" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && <MenuOverlay onClose={() => setOpen(false)} />}
      </AnimatePresence>
    </>
  );
};
